/**
 * Every lift refusal code, by what it means to someone in the hotel (twin
 * slice 1, W5).
 *
 * The codes are the adapters-lift vocabulary
 * (`tests/fixtures/contracts/lift-refusal-codes.json`, held to the source
 * by a drift test): the edge's ack codes plus the ones the lift-session
 * adapter synthesises. A bijection test keeps this table and that fixture
 * the same set, so a new code cannot reach a hotel toast as a raw token.
 *
 * Used by the hotel-word toasts for `lift_refusal`, which name the code's
 * CLASS and never the code. A car's live Guests and Robots lines are W6's
 * (`liftReading()`), not this table's.
 */

export type LiftCodeClass =
  /** The write gate: lift control is switched off. */
  | 'gate'
  /** The car is on a setting someone chose (Independent Service,
   *  Inspection...), or a mode robots may not call under. */
  | 'staff_mode'
  /** An emergency mode the bridge must never write against. */
  | 'emergency'
  /** The platform cannot reach the car's controller, or lost it mid-ride. */
  | 'link'
  /** Fire service has the car, or the platform stood off it in a fire. */
  | 'fire'
  /** One robot's ride, paused until a person resumes it; the car itself
   *  is usable by every other robot. */
  | 'latched'
  /** The request named something the controller does not serve. */
  | 'request'
  /** One operator press that did not apply; never the lift's standing
   *  state. */
  | 'operator'
  /** A ride that was admitted and then failed. */
  | 'failure';

export const LIFT_CODE_CLASS: Readonly<Record<string, LiftCodeClass>> = {
  WRITES_NOT_ARMED: 'gate',
  INVENTORY_NOT_VALIDATED: 'gate',
  ACCEPT_GATE_DISABLED: 'gate',
  UNKNOWN_LIFT: 'request',
  UNSERVED_FLOOR: 'request',
  INVALID_PAYLOAD: 'request',
  TELEMETRY_STALE: 'link',
  LINK_DOWN: 'link',
  HEARTBEAT_LOST: 'link',
  ALREADY_IN_SESSION: 'operator',
  NO_SESSION: 'operator',
  WRONG_PHASE: 'operator',
  NOTHING_WAITING: 'operator',
  NOT_ADVANCEABLE: 'operator',
  MODE_NOT_PERMITTED: 'staff_mode',
  LIFT_MODE_NOT_NORMAL: 'staff_mode',
  HIGHER_PRIORITY_MODE: 'emergency',
  SEQUENCE_TIMEOUT: 'failure',
  SEQUENCE_CANCELLED: 'failure',
  WRITE_FAILED: 'failure',
  INTERNAL: 'failure',
  FIRE_RECALL: 'fire',
  FIRE_EVACUATION: 'fire',
  RIDE_LATCHED: 'latched',
};

/** The class of a code; an unknown code reads as a plain refusal. */
export function liftCodeClass(code: string | null | undefined): LiftCodeClass {
  return (code && LIFT_CODE_CLASS[code]) || 'failure';
}

/** What a refused lift means, in the hotel's words. COPY STATUS: pending
 *  Akash's copy pass 2. */
export function liftRefusalWords(lift: string, cls: LiftCodeClass): string {
  switch (cls) {
    case 'gate':
      return `Robots can't use ${lift}: lift control is switched off`;
    case 'staff_mode':
      return `${lift} is on a staff setting, so robots can't board it`;
    case 'emergency':
      return `${lift} is in an emergency mode, so robots can't use it`;
    case 'link':
      return `Nexsoss can't reach ${lift}'s controller, so robots aren't using it`;
    case 'fire':
      return `Robots can't use ${lift} during the fire alarm`;
    case 'latched':
      // About ONE robot's ride, never the car: the adapter will not call
      // the car for that robot until someone resumes the ride, and the car
      // is "a perfectly usable car for every OTHER robot" -- which is why
      // `liftReading` reads it "Allowed" (adapter `core.py::RIDE_LATCH_CODE`).
      return `A robot's ride in ${lift} is paused until Nexsoss resumes it; other robots can still use ${lift}`;
    case 'request':
    case 'operator':
    case 'failure':
      return `${lift} is turning robots away`;
  }
}

/**
 * Refusal codes the server's `lift_refusal` never raises an alert for:
 * `alerts_engine._GATE_CODES` and `_OPERATOR_VERB_CODES`. Held to the
 * engine by the contract fixture (`lift-refusal-codes.json`
 * `api_server`, checked against the engine by the backend's
 * `test_alert_contract_fixtures.py`), to that fixture by
 * `attention-lift-items.test.ts`, and to the engine's source text by
 * `attention-engine-codes.test.ts`. `ACCEPT_GATE_DISABLED` is deliberately
 * NOT here: the engine still raises it (the recorded divergence).
 */
export const LIFT_REFUSAL_NOT_ALERTED: ReadonlySet<string> = new Set([
  'WRITES_NOT_ARMED',
  'INVENTORY_NOT_VALIDATED',
  'ADAPTER_DEADLINE',
  'AGV_NOT_GRANTED',
  'HELD_BY_ROBOT',
  'IN_FLIGHT',
  'NOTHING_WAITING',
  'NOT_ADVANCEABLE',
  'NO_SESSION',
  'NO_VERDICT',
  'WRONG_OWNER',
  'WRONG_PHASE',
]);

/**
 * Closure codes W7's `lift_lanes_closed` never raises for, because another
 * alert or panel says them: the write gate INCLUDING
 * `ACCEPT_GATE_DISABLED`, the adapter's link codes, the two fire codes,
 * and the operator-verb codes. This is W7's
 * `_LIFT_LANES_CLOSED_EXCLUDED_CODES`, held to the engine's source text by
 * `tests/unit/attention-engine-codes.test.ts` now that both are in one
 * tree.
 */
export const LANES_CLOSED_NOT_ALERTED: ReadonlySet<string> = new Set([
  ...LIFT_REFUSAL_NOT_ALERTED,
  'ACCEPT_GATE_DISABLED',
  'LINK_DOWN',
  'TELEMETRY_STALE',
  'FIRE_RECALL',
  'FIRE_EVACUATION',
]);
